import { FormTypes } from "../../../utils/models";
import { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";

import AboutHelpModal from "./AboutHelpModal";
import Alert from "@mui/material/Alert";
import Confirm1040 from "./Confirm1040";
import PromptNoTaxableIncome from "./PromptNoTaxableIncome";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import CustomStepper from "../../../components/CustomStepper";
import FormLayout from "../../../components/FormLayout";
import InfoCircleIcon from "@mui/icons-material/InfoRounded";
import LineLoader from "../../../components/Loaders/LineLoader/LineLoader";
import { push } from "redux-first-history";
import { taxSlice, TaxState } from "../../../store/reducer/tax";
import { RootState } from "../../../store/index";
import {
  Confirm1040Task,
  Delete1040Task,
  selectTask,
  Upload1040Task,
} from "../../../utils/tasks";
import { User } from "../../../model/user";
import { ErrorBoundary } from "react-error-boundary";
import { FileRejection } from "react-dropzone";
import FileDropContainer from "../../../components/FileInputWithDragAndDrop/FileDropContainer";
import { ErrorAlert } from "../../../components/Alerts";

const steps = ["upload 1040", "confirm"];
interface Forms1040Props {
  taxState: TaxState;
  user: User | null;
}

const Forms1040 = ({ user, taxState }: Forms1040Props) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState<File | { name: string }>();
  const [open, setOpen] = useState(false);
  const [editMode, toggleEditMode] = useState(false);
  const [fileDropError, setFileDropError] = useState("");
  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    setFileDropError("Upload failed: " + rejectedFiles[0].errors[0].message);
    setTimeout(() => setFileDropError(""), 4000);
  };
  const { show1040NoIncomePrompt, failureCount } = taxState;
  const upload1040Task = selectTask(taxState, Upload1040Task.id);
  const confirm1040Task = selectTask(taxState, Confirm1040Task.id);
  const delete1040Task = selectTask(taxState, Delete1040Task.id);
  const currentTaxYear = useSelector((state: RootState) => state.state.taxYear);
  useEffect(() => {
    dispatch(taxSlice.actions.fetch1040FormDetails());
  }, [dispatch]);

  const handleNext = () => {
    if (!taxState.irs1040) {
    } else {
      dispatch(taxSlice.actions.validate1040Income());
    }
  };

  const onDropAccepted = (acceptedFiles: any) => {
    setFile(acceptedFiles[0]);
    dispatch(taxSlice.actions.upload1040Request(acceptedFiles[0] as File));
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const deleteFile = () => {
    if (taxState.t1040) {
      dispatch(taxSlice.actions.delete1040Request());
    }
    setFile(undefined);
    toggleEditMode(false);
  };

  const renderStepContent = () => {
    if (!taxState.irs1040 || taxState.irs1040.document_name === "error") {
      if (!taxState.t1040) {
        return <LineLoader />;
      }
      return (
        <FormLayout
          title={"Federal Form 1040"}
          subtitle={"File upload"}
          description={
            <span style={{color: '#0f5757', fontWeight: 700}}>
                Great! Next, we’ll need you to upload the first page of your Federal Form 1040 for the current tax filing year {currentTaxYear} (no need to include Page 2).
            </span>
          }
          className="mb-4 mt-2"
          icon={
            <ContactSupportIcon titleAccess="Help" className="help-icon" onClick={handleOpen} />
          }
        >
          <FileDropContainer
            uploadTask={upload1040Task}
            onDropAccepted={onDropAccepted}
            onDropRejected={onDropRejected} />
          {/*
              NOTE: Springboro have since changed their minds on how no income is handled
              Please see validate1040Income TaxSaga to hook into no income handling

              noTaxableIncome && (
            <>
              <Alert
                severity={"error"}
                variant="outlined"
                classes={{
                  root: 'tax-0',
                  icon: 'error-icon',
                }}
                iconMapping={{
                  error: <InfoCircleIcon />,
                }}
              >
                It looks like you don’t have any income that is taxable to the
                city. Your filing this year will be complete once you
                digitally sign this return. If you are permanently retired or
                disabled, check the box below and you will be exempted from
                filing with us in the future.
              </Alert>
              <GreenCircleCheckbox
                name="retired_disabled"
                checked={user?.is_retired}
                onChange={() =>
                  dispatch(userSlice.actions.retiredOrDisabled(!user?.is_retired))
                }
                label="I am permanently retired or disabled."
                labelClass='chkbox'
                rootClass='label-root'
              />
            </>
            ) */}
          {fileDropError && (
            <ErrorAlert floating>{fileDropError}</ErrorAlert>
          )}
          {taxState.irs1040 && (
            <Alert
              severity={"error"}
              variant="outlined"
              classes={{
                root: "error-alert",
                message: "success-msg",
                icon: "error-icon",
              }}
              iconMapping={{ error: <InfoCircleIcon /> }}
            >
              {taxState.irs1040.message}
            </Alert>
          )}
        </FormLayout>
      );
    } else {
      return (
        <ErrorBoundary fallbackRender={() => <></>}>
          <Confirm1040
            editMode={editMode}
            toggleEditMode={toggleEditMode}
            deleteFile={deleteFile}
            handleOpen={handleOpen}
          />
          <PromptNoTaxableIncome
            show={show1040NoIncomePrompt}
            onCancel={() => {
              dispatch(taxSlice.actions.show1040NoIncomePrompt(false));
            }}
            onContinue={() => {
              dispatch(taxSlice.actions.show1040NoIncomePrompt(false));
              dispatch(taxSlice.actions.confirm1040Request());
            }}
          />

          {taxState.tax_return?.IRS1040.messages?.map((message) => (
            <ErrorAlert>{message}</ErrorAlert>
          ))}
        </ErrorBoundary>
      );
    }
  };

  const isNextEnabled = () => {
    if (!taxState.irs1040) {
      return !file;
    }
    return editMode;
  };

  return (
    <>
      <CustomStepper
        steps={steps}
        activeStep={taxState.irs1040 ? 1 : 0}
        handleNext={handleNext}
        handleBack={() => dispatch(push("/user/review"))}
        renderStepContent={renderStepContent}
        className="upload-stepper"
        continueButtonLabel="confirm"
        nextBtnDisable={isNextEnabled()}
        showBackButton={true}
        disableSteps={true}
      />
      <AboutHelpModal
        open={open}
        formType={FormTypes.t1040}
        handleClose={handleClose}
      />
      {(upload1040Task || confirm1040Task || delete1040Task) && <LineLoader />}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  taxState: state.tax,
  user: state.user.data,
});

export default connect(mapStateToProps)(Forms1040);
